/**
 * Pulse wall — the single cursor-following tooltip layer (the prototype's
 * "FloatingTip"). Extracted from `pulse-wall.tsx` (#1144).
 *
 * ONE tip for the WHOLE wall — not a radix <Tooltip> per bar, nor a separate one on
 * each blank Setup door — which is what makes EVERY hover both FOLLOW THE CURSOR and
 * STAY OPEN. Each bar carries `data-pulse-bar={id}` and each section is a
 * `data-pulse-cluster={key}` box (live OR blank). WallTipLayer owns the hovered
 * target (a bar id, or a blank section) + cursor position as state and renders one
 * portal'd card at the pointer:
 *   · over a live bar              → that bar's stats, following the cursor.
 *   · live section chrome / gaps   → keeps the active bar's tip following (covers the
 *                                    2px inter-bar gaps + a card's pass-through link).
 *   · over a BLANK section's door  → that section's "not instrumented · N planned"
 *                                    tip, ALSO following (no stationary "snap").
 *   · leaving the wall             → closes (onMouseLeave).
 * Because the card is decoupled from the bar DOM and resolves its content from maps
 * rebuilt off the same data, an SWR revalidation that remounts the bars refreshes the
 * open tip IN PLACE instead of flashing it shut.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Portal } from "radix-ui";
import { Elevated } from "../../elevated";
import { band, BAND, bandState, isSuggested } from "./pulse-wall-shared";
import type { PulseGroupDTO, PulseTouchDTO } from "../../api/contracts/pulse";

const KIND_LABEL: Record<string, string> = {
  automation: "Automation",
  stage: "Stage",
  channel: "Channel",
  signal: "Signal",
  metric: "Metric",
  queue: "Queue",
  task: "Task",
  integration: "Integration",
};

type BarTipEntry = { touch: PulseTouchDTO; groupLabel: string };
type ClusterMeta = { label: string; planned: number; blank: boolean };
type Hover = { id: string | null; ck: string | null; x: number; y: number };

/** Index every drawn bar (live + planned ghost; suggested drafts aren't drawn as
 *  bars) by its `data-pulse-bar` id, so the floating tip resolves a hover in O(1). */
function buildBarIndex(groups: PulseGroupDTO[]): Map<string, BarTipEntry> {
  const m = new Map<string, BarTipEntry>();
  for (const g of groups) {
    // An uninstrumented section draws a Setup door, not bars — so its planned-ghost
    // touches never carry a `data-pulse-bar` node. Skip them: the index mirrors
    // exactly what's drawn (the blank section's hover is served by byCluster instead).
    if (bandState(g) === "uninstrumented") continue;
    for (const t of g.touches) {
      if (!isSuggested(t)) m.set(t.id, { touch: t, groupLabel: g.label });
    }
  }
  return m;
}

/** Index every section by its `data-pulse-cluster` key — its label, planned-touchpoint
 *  count, and whether it's a BLANK (uninstrumented) section drawn as a Setup door — so
 *  a blank section's hover renders the same cursor-following way as a bar's. */
function buildClusterIndex(groups: PulseGroupDTO[]): Map<string, ClusterMeta> {
  const m = new Map<string, ClusterMeta>();
  for (const g of groups) {
    const planned = g.touches.filter((t) => t.health == null && !isSuggested(t)).length;
    m.set(g.key, { label: g.label, planned, blank: bandState(g) === "uninstrumented" });
  }
  return m;
}

/**
 * Exported so other walls (e.g. the home's `GranularPulse`) can reuse the EXACT
 * cursor-following tooltip: wrap your bars, tag each bar element with
 * `data-pulse-bar={touch.id}` and each cluster with `data-pulse-cluster={group.key}`,
 * and pass the same `groups` — the layer resolves the hovered bar/blank-section and
 * renders the one portal'd tip that follows the pointer.
 */
export function WallTipLayer({
  groups,
  className,
  children,
  tipSide = "above",
}: {
  groups: PulseGroupDTO[];
  className?: string;
  children: React.ReactNode;
  /** Which side of the cursor the tip prefers. Default "above" (flips below near
   *  the top edge); "below" prefers under the cursor (flips above near the bottom).
   *  A top-nav wall wants "below" so the card doesn't cover the bar. */
  tipSide?: "above" | "below";
}) {
  const byId = useMemo(() => buildBarIndex(groups), [groups]);
  const byCluster = useMemo(() => buildClusterIndex(groups), [groups]);
  const [hover, setHover] = useState<Hover | null>(null);
  // Keep the card mounted through a short fade+scale exit so it doesn't blink out
  // (the jarring instant-unmount). `shown` mirrors the live hover while present,
  // then lingers with `open:false` for the exit animation before unmounting. A
  // re-enter during that window cancels the pending unmount and animates back in.
  const [shown, setShown] = useState<{ hover: Hover; open: boolean } | null>(null);
  useEffect(() => {
    if (hover) {
      setShown({ hover, open: true });
      return;
    }
    setShown((p) => (p && p.open ? { hover: p.hover, open: false } : p));
    const t = setTimeout(() => setShown(null), 160);
    return () => clearTimeout(t);
  }, [hover]);

  // `children` are built by the (hover-agnostic) parent, so a pointer move only
  // re-renders THIS layer + the portal tip — the bars keep element identity and
  // React skips them. No per-move re-render of the whole wall.
  function onMouseMove(e: React.MouseEvent) {
    const el = e.target as HTMLElement | null;
    // The section the cursor is in, by its `data-pulse-cluster` marker — the same
    // source for a bar and for the chrome / blank door around it, so they agree.
    const clusterEl = el?.closest?.("[data-pulse-cluster]") as HTMLElement | null;
    const ck = clusterEl?.getAttribute("data-pulse-cluster") ?? null;
    // 1) Direct hit — the cursor is over a bar element (the hero + seamless walls,
    //    where the bars sit lifted on top and catch pointer events themselves).
    const bar = el?.closest?.("[data-pulse-bar]") as HTMLElement | null;
    if (bar) {
      const id = bar.getAttribute("data-pulse-bar");
      if (id && byId.has(id)) {
        setHover({ id, ck, x: e.clientX, y: e.clientY });
        return;
      }
    }
    const cm = ck ? byCluster.get(ck) : null;
    // Over a BLANK section's Setup door → show THAT section's tip, also following the
    // cursor (the fix for the door's old stationary "snap to a single point").
    if (cm && cm.blank) {
      setHover({ id: null, ck, x: e.clientX, y: e.clientY });
      return;
    }
    // 2) Geometry fallback — the cursor is inside a LIVE section but a covering
    //    element (not a bar) is the event target, so step 1's `closest` couldn't
    //    reach the bar beneath it. The motivating case is the compact sub-page
    //    card (the strip on `/work?area=queue`): its stretched section <Link> sits
    //    over un-lifted bars to keep "the whole card navigates", which used to
    //    suppress the per-bar tip. (On the hero/seamless/mini walls the bars catch
    //    their own hover, so step 1 returns and this only runs in the header/footer/
    //    inter-bar gaps — where the rect test below correctly finds nothing.)
    //    Resolve the hovered bar by COLUMN: the `data-pulse-bar` whose box contains
    //    the pointer. Each bar element is the full-track-height wrapper (the short
    //    colored fill is a child), so this matches anywhere in its column even at low
    //    health, and naturally excludes the header/footer (cursor above/below every
    //    bar box) — giving the strip the same per-bar tip as the hero without lifting
    //    the bars (which would steal the card's whole-surface click).
    if (clusterEl && cm && !cm.blank) {
      const bars = clusterEl.querySelectorAll("[data-pulse-bar]");
      for (let i = 0; i < bars.length; i++) {
        const be = bars[i] as HTMLElement;
        const id = be.getAttribute("data-pulse-bar");
        if (!id || !byId.has(id)) continue;
        const r = be.getBoundingClientRect();
        if (e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom) {
          setHover({ id, ck, x: e.clientX, y: e.clientY });
          return;
        }
      }
    }
    // 3) A live section's non-bar chrome / inter-bar gaps: keep the active tip following
    // while still in the SAME section; crossing into a different one, or leaving the
    // wall, closes it — so a section's tip never lingers over a neighbor.
    setHover((p) => {
      if (!p) return p;
      if (ck && ck === p.ck) return { id: p.id, ck: p.ck, x: e.clientX, y: e.clientY };
      return null;
    });
  }

  // Keyboard parity: focusing a bar (or a blank Setup door) surfaces the same tip,
  // anchored to the element's box (not the cursor). React's onFocus/onBlur bubble
  // (focusin/focusout), so the wrapper catches focus onto any descendant.
  function onFocus(e: React.FocusEvent) {
    const el = e.target as HTMLElement | null;
    if (!el) return;
    const bar = el.closest?.("[data-pulse-bar]") as HTMLElement | null;
    if (bar) {
      const id = bar.getAttribute("data-pulse-bar");
      if (!id || !byId.has(id)) return;
      const ck = (bar.closest("[data-pulse-cluster]") as HTMLElement | null)?.getAttribute("data-pulse-cluster") ?? null;
      const r = bar.getBoundingClientRect();
      setHover({ id, ck, x: Math.round(r.left + r.width / 2), y: Math.round(r.top) });
      return;
    }
    const ck = (el.closest?.("[data-pulse-cluster]") as HTMLElement | null)?.getAttribute("data-pulse-cluster") ?? null;
    const cm = ck ? byCluster.get(ck) : null;
    if (cm && cm.blank) {
      const r = el.getBoundingClientRect();
      setHover({ id: null, ck, x: Math.round(r.left + r.width / 2), y: Math.round(r.top) });
    }
  }

  const close = () => setHover((p) => (p ? null : p));

  // Resolve the hovered target to content — a bar's stats, or a blank section's
  // summary — looked up fresh each render, so a revalidation updates the open tip
  // in place rather than flashing it.
  let tip: React.ReactNode = null;
  if (shown) {
    const hv = shown.hover;
    if (hv.id != null) {
      const e = byId.get(hv.id);
      if (e) {
        tip = (
          <TipCard x={hv.x} y={hv.y} open={shown.open} side={tipSide}>
            <BarTipContent touch={e.touch} groupLabel={e.groupLabel} />
          </TipCard>
        );
      }
    } else {
      const cm = hv.ck ? byCluster.get(hv.ck) : null;
      if (cm && cm.blank) {
        tip = (
          <TipCard x={hv.x} y={hv.y} open={shown.open} side={tipSide}>
            <BlankTipContent label={cm.label} planned={cm.planned} />
          </TipCard>
        );
      }
    }
  }

  return (
    <div className={className} onMouseMove={onMouseMove} onMouseLeave={close} onFocus={onFocus} onBlur={close}>
      {children}
      {tip}
    </div>
  );
}

/** The portal'd hover card, positioned to follow the cursor (centered horizontally,
 *  sitting just above the pointer). Clamped to the viewport on BOTH axes — the old
 *  radix tooltip had avoidCollisions, so this must flip below the cursor when the
 *  anchor is near the top edge (a mini wall in an Object header, or a scrolled-up
 *  wall), else the card draws off-screen. `pointer-events-none` so it never steals
 *  the mouse — which would otherwise fire a leave → re-enter flicker loop. Content
 *  (a bar's stats or a blank section's summary) is supplied as children.
 *
 *  Animation: the OUTER node owns positioning + the centering translate; the INNER
 *  card owns the fade+scale so its transform never clobbers the centering one (the
 *  tw-animate-css transform-collision class — see the dialog corner-slide fix). It
 *  eases opacity+scale on `data-open`, and the parent keeps it mounted with
 *  `open=false` for the exit so the card sinks back toward the bar instead of
 *  blinking out. Scales from the edge nearest the cursor (origin flips with the flip). */
function TipCard({ x, y, open, side = "above", children }: { x: number; y: number; open: boolean; side?: "above" | "below"; children: React.ReactNode }) {
  const HALF = 116; // ≈ half the w-52 card + a hair of padding
  const GAP = 12;
  const EST_H = 100; // generous estimate of the card height (for the edge flips)
  const vw = typeof window !== "undefined" ? window.innerWidth : x + HALF;
  const vh = typeof window !== "undefined" ? window.innerHeight : y + EST_H + GAP;
  const left = Math.min(Math.max(x, HALF), vw - HALF);
  const roomAbove = y - GAP - EST_H >= 0;
  const roomBelow = y + GAP + EST_H <= vh;
  // Default prefers ABOVE (flip below near the top edge — unchanged); side="below"
  // prefers UNDER the cursor (flip above only when there's no room below). Never
  // draws off-screen either way.
  const below = side === "below" ? roomBelow || !roomAbove : !roomAbove;
  const top = below ? y + GAP : y - GAP;
  const transform = below ? "translate(-50%, 0)" : "translate(-50%, -100%)";
  return (
    <Portal.Root>
      <div className="pointer-events-none fixed z-50" style={{ left, top, transform }}>
        <Elevated offset={2} shadowLevel={3} asChild>
          <div
            role="tooltip"
            data-open={open}
            className={`rounded-md px-3 py-2 type-body text-foreground transition-[opacity,transform] duration-150 ease-out data-[open=true]:opacity-100 data-[open=true]:scale-100 data-[open=false]:opacity-0 data-[open=false]:scale-95 ${below ? "origin-top" : "origin-bottom"}`}
          >
            {children}
          </div>
        </Elevated>
      </div>
    </Portal.Root>
  );
}

/** The tip body — title · kind · value vs target with delta · subjects · at-risk.
 *  A ghost bar (no health) shares the uninstrumented body so the card is the same
 *  size whether you're over a measured bar or an un-measured one. */
function BarTipContent({ touch, groupLabel }: { touch: PulseTouchDTO; groupLabel: string }) {
  if (touch.health == null) {
    return <UninstrumentedBody title={`${groupLabel} · ${touch.label}`} />;
  }
  const h = touch.health;
  const b = band(h, touch.target);
  const delta = h - touch.target;
  const sign = delta >= 0 ? "+" : "";
  const trendWord =
    touch.trend === "up" ? "improving" : touch.trend === "down" ? "regressing" : touch.trend === "flat" ? "flat" : null;
  return (
    <div className="w-52">
      <div className="type-body font-medium">
        {groupLabel} · {touch.label}
      </div>
      <div className="type-subtext text-muted-foreground">{KIND_LABEL[touch.kind] ?? touch.kind}</div>
      <div className="mt-1.5 flex items-baseline gap-2">
        <span className={`type-title tabular-nums ${BAND[b].fg}`}>{h}</span>
        <span className="type-subtext text-muted-foreground">
          vs target {touch.target} ·{" "}
          <span className={delta >= 0 ? "text-success" : "text-danger"}>
            {sign}
            {delta}
          </span>
        </span>
      </div>
      <div className="mt-0.5 type-subtext tabular-nums text-muted-foreground">
        {touch.activeCount} {touch.activeCount === 1 ? "subject" : "subjects"} · {touch.atRisk} at-risk
        {trendWord ? ` · ${trendWord}` : ""}
      </div>
    </div>
  );
}

/** The tip body for a BLANK (uninstrumented) section — its label and the count of
 *  planned touchpoints the Setup door stands in for (the §8.4 "don't hide what could
 *  be here", now cursor-following instead of a stationary radix tooltip). */
function BlankTipContent({ label, planned }: { label: string; planned: number }) {
  return <UninstrumentedBody title={label} planned={planned} />;
}

/** Shared body for any UNINSTRUMENTED hover — a ghost bar OR a blank section door.
 *  Mirrors BarTipContent's four-row rhythm (title · sub · big value · detail) at the
 *  same `w-52` width, so the card never shrinks as the cursor crosses between a
 *  measured bar and an un-measured one. Instead of a single short "not instrumented"
 *  line, it carries real Setup state: a status tag, an em-dash placeholder where the
 *  health number would sit, and a "set up to measure" next step (with the planned
 *  count when the section stands in for planned touchpoints). */
function UninstrumentedBody({ title, planned }: { title: string; planned?: number }) {
  return (
    <div className="w-52">
      <div className="type-body font-medium">{title}</div>
      <div className="type-subtext text-muted-foreground">Setup</div>
      <div className="mt-1.5 flex items-baseline gap-2">
        <span className="type-title tabular-nums text-muted-foreground">—</span>
        <span className="type-subtext text-muted-foreground">not instrumented yet</span>
      </div>
      <div className="mt-0.5 type-subtext tabular-nums text-muted-foreground">
        {planned && planned > 0
          ? `${planned} planned ${planned === 1 ? "touchpoint" : "touchpoints"} · set up to measure`
          : "Set up to start measuring"}
      </div>
    </div>
  );
}
